import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { DataTable, IDataTableColumn } from '../../components/common/DataTable';
import { eBaseActionStatus } from '../../context/base/eBaseActionStatus';
import { BlockCard } from '../../components/container';
import { Button, ButtonLink, LoadingTable } from '../../components/base';
import { BannerConfigModel } from '../../context/models/banners/BannerConfigModel';
import { BaseShowDetail } from '../../context/base/BaseShowDetail';
import { actions } from '../../context/stores/rootActions';
import { AppState } from '../../context/stores/rootReducers';
import { useLookupBannerPosition } from '../../components/shared/hoc-banner-position/useLookupBannerPosition';
import { ProductCategoryModel } from '../../context/models/products-category/ProductCategoryModel';
import BannerConfigModal from './BannerConfigModal';

type IBannerConfigTableProps = {
	bannerId:number;
	positionId:number;
	listPageId:number
};

const BannerConfigTable = ({ bannerId, positionId, listPageId}: IBannerConfigTableProps) => {
	const dispatch = useDispatch();
	const { status, allBannerConfigs, showConfirm, selectedIds } = useSelector((state: AppState) => state.bannerConfig);
	const { showDetail } = useSelector((state: AppState) => state.productCategory);


	const dataSources = useMemo(() => {
		return allBannerConfigs.sort((a, b) => a.id - b.id);
	}, [allBannerConfigs]);

	const handleReloadDataWithFilter = useCallback(
		() => {
			dispatch(actions.bannerPosition.getAllRequest());
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const initBannerConfig = useMemo(() => {
		return {
			id: 0,
			bannerId: bannerId,
			positionId: positionId,
			listPageId: listPageId,
		} as unknown as BannerConfigModel;
	}, [bannerId, positionId, listPageId]);

	const handleSaveData = (data: BannerConfigModel) => {
		dispatch(actions.bannerConfig.saveRequest(data));
	};

	const { banner_position_all } = useLookupBannerPosition();

	const onChangeSelectedIds = (ids: number[]) => {
		dispatch(actions.bannerConfig.changeSelectedIds(ids));
	};
	const onShowDetail = (data: BaseShowDetail<ProductCategoryModel>) => {
		dispatch(actions.productCategory.showDetail(data));
	};

	useEffect(() => {
		handleReloadDataWithFilter();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bannerId]);

	useEffect(() => {
		if (status == eBaseActionStatus.reload) handleReloadDataWithFilter();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const columns = useMemo((): IDataTableColumn<BannerConfigModel>[] => {
		return [
			{
				accessorKey: 'positionId',
				header: 'Vị trí',
				minSize: 150,
				cell: ({ getValue }) => {
					const val = getValue() as number;
					const pos = banner_position_all.find((p: any) => p.id === val);
					return <span className='font-medium'>{pos ? pos.name : val}</span>;
				}
			},
			{
				id: 'actions',
				header: 'Trang hiển thị',
				meta: { align: 'center' },
				size: 120,
				cell: ({ row }) => (
					<ButtonLink
						onClick={() => onShowDetail({ isShow: true, detailData: row.original as any })}
						title='Trang hiển thị'
						icon='ni ni-edit'
						theme='link'
					/>
				)
			}
		];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [banner_position_all]);

	return (
		<>
			<div className='w-full'>
				{bannerId > 0 && (
					<BlockCard>
						{status === eBaseActionStatus.loading && <LoadingTable />}
						{status !== eBaseActionStatus.loading && (
							<DataTable
								data={dataSources}
								columns={columns}
								isLoading={status !== eBaseActionStatus.complete}
								enableRowSelection
								rowSelection={selectedIds.reduce((acc, id) => ({ ...acc, [String(id)]: true }), {})}
								onRowSelectionChange={(selection) => {
									onChangeSelectedIds(Object.keys(selection).filter(k => selection[k]).map(Number));
								}}
								enablePagination
								totalRecords={dataSources.length}
								enableGlobalFilter
								toolbar={{ isShowSearch: true }}
								onRefresh={handleReloadDataWithFilter}
							/>
						)}
					</BlockCard>
				)}
				{bannerId <= 0 && <div className='alert alert-warning'>Vui lòng lưu Banner để bổ sung vị trí Banner</div>}
			</div>
			{showDetail && showDetail.isShow && (
				<>
					<BannerConfigModal
						isSaving={status === eBaseActionStatus.saving}
						isShow={showDetail.isShow}
						onClose={() => onShowDetail({ isShow: false })}
						onSubmit={(data) => handleSaveData(data)}
						title={`${showDetail.detailData ? `Cập nhật` : `Thêm mới`} trang hiển thị`}
					/>
				</>
			)}
		</>
	);
};

export default BannerConfigTable;
